import React, { useState } from "react";
import { HiChevronDown, HiChevronRight } from 'react-icons/hi';
import RebalanceItem from "../../../api/models/rebalanceItem";
import { isFund, isMobile, positionDescription, rebalanceQuantityString, rebalanceNotionalString, positionQuantityString, positionNotionalString, positionPriceString, componentPercentageString } from "../../../app/utils";
import Position from "../../../api/models/portfolioPosition";
import DeviationGauge from "../deviation-gauge";

interface PortfolioRebalanceItemProps {
  rebalanceItem: RebalanceItem,
  newInvestmentTotal: number
}

const PortfolioRebalanceItem: React.FC<PortfolioRebalanceItemProps> = (props: PortfolioRebalanceItemProps) => {

  const [expanded, setExpanded] = useState(false);
  const [done, setDone] = useState(props.rebalanceItem.done);

  const textSide = props.rebalanceItem.quantity > 0 ? 'Comprar' : 'Vender';
  const textColor = props.rebalanceItem.quantity > 0 ? 'text-green-400 font-semibold' : 'text-red-400 font-semibold';
  const checkboxColor = props.rebalanceItem.quantity > 0 ? 'accent-green-400' : 'accent-red-400';
  const bgColor = '';
  const bgColorDetails = 'bg-gradient-to-r from-black/25 to-transparent to-50%';

  const maskTransparentRightSide = '[mask-image:linear-gradient(to_right,black_0,black_50px,black_calc(100%-50px),transparent_100%)]';
  const maskTransparentLeftSide = '[mask-image:linear-gradient(to_right,transparent_0,black_50px,black_calc(100%-50px),black_100%)]';

  const newPosition = new Position(
    props.rebalanceItem.position.id,
    props.rebalanceItem.position.portfolioId,
    props.rebalanceItem.position.security,
    props.rebalanceItem.position.description,
    props.rebalanceItem.position.quantity + props.rebalanceItem.quantity,
    props.rebalanceItem.position.price,
    props.rebalanceItem.position.priceDate,
    new Date(),
    props.rebalanceItem.position.weight,
    props.rebalanceItem.position.parentCompositeId
  );
  newPosition.setTotalPortfoliolNotional(props.rebalanceItem.position.totalPortfolioNotional + props.newInvestmentTotal);

  return (props.rebalanceItem.quantity &&
    <>
      <tr className={`${bgColor} group cursor-pointer`}>
        <td onClick={() => setExpanded(!expanded)} className='pl-2'>
          {!expanded && <HiChevronDown size={isMobile() ? 18 : 20} />}
          {expanded && <HiChevronRight size={isMobile() ? 18 : 20} />}
        </td>
        <td onClick={() => setExpanded(!expanded)} className={`${done ? 'line-through' : textColor} uppercase items-center text-start pl-2 pt-3`}>{textSide}</td>
        {
          !(isFund(props.rebalanceItem.position.security?.assetType) || props.rebalanceItem.position.security === undefined) &&
          <td
            onClick={() => setExpanded(!expanded)}
            className={`${done ? 'line-through' : ''} uppercase items-center text-end px-3 pt-3`}
          >
            {rebalanceQuantityString(props.rebalanceItem)}
          </td>
        }
        <td
          onClick={() => setExpanded(!expanded)}
          colSpan={isFund(props.rebalanceItem.position.security?.assetType) || props.rebalanceItem.position.security === undefined ? 2 : 1}
          className={`${done ? 'line-through' : ''} uppercase items-center text-end px-3 pt-3 max-w-16 lg:max-w-fit`}
        >
          {positionDescription(props.rebalanceItem.position)}
        </td>
        <td className={`${checkboxColor} items-center text-center pr-2 pt-3`}>
          <input type='checkbox' checked={done} onClick={() => setDone(!done)} className="cursor-pointer w-4 h-4 lg:w-6 lg:h-6 mt-1" />
        </td>
      </tr>
      <tr onClick={() => setExpanded(!expanded)} className={`${bgColor} cursor-pointer`}>
        <td colSpan={4} className={`${done ? 'line-through' : ''} pt-0 px-3 pb-2 items-center text-end text-xs border-b border-blue-300/40`}>
          {
            'Total: R$ ' + rebalanceNotionalString(props.rebalanceItem)
          }
        </td>
        <td className='p-0 border-b border-blue-300/40' />
      </tr>
      <tr>
        <td onClick={() => setExpanded(!expanded)} colSpan={5} className={`${expanded ? 'h-fit border-b' : 'h-0'} p-0 border-blue-300/40 transition-all`}>
          <div className={`${expanded ? 'py-5' : 'h-0 w-0'} ${bgColorDetails} relative flex flex-row px-5 lg:px-36 text-sm lg:text-lg overflow-hidden transition-all`}>

            <div className="absolute flex justify-center items-center left-0 top-0 w-full h-full opacity-20">
              <DeviationGauge
                deviation={props.rebalanceItem.position.deviation()}
                deviationLimit={0.3}
                animate={false}
                className={'w-3/5 lg:w-1/5 flex items-center justify-center'}
                valueClassName='text-center w-full mt-2 bg-transparent opacity-50'
              />
            </div>

            {/* Left (Before Rebalance) Column */}
            <div className="flex flex-col w-1/3 text-start">
              <label className="w-full text-transparent">.</label>
              {/* Quantidade */}
              <label className="w-full pt-5">Antes</label>
              <div className={`w-full border-b my-1 border-blue-300/40 ${maskTransparentLeftSide}`} />
              <label className="w-full">{positionQuantityString(props.rebalanceItem.position)}</label>
              {/* Preço */}
              <label className="w-full pt-5">Antes</label>
              <div className={`w-full border-b my-1 border-blue-300/40 ${maskTransparentLeftSide}`} />
              <label className="w-full">{"R$ " + positionNotionalString(props.rebalanceItem.position)}</label>
              {/* % do Portfolio */}
              <label className="w-full pt-5">Antes</label>
              <div className={`w-full border-b my-1 border-blue-300/40 ${maskTransparentLeftSide}`} />
              <label className="w-full">{componentPercentageString(props.rebalanceItem.position)}</label>
            </div>

            {/* Middle (Rebalance) Column */}
            <div className="flex flex-col w-1/3 text-center">
              <label className={`${done ? 'line-through' : textColor} w-full`}>{textSide}</label>
              {/* Quantidade */}
              <label className="w-full pt-5">Quantidade</label>
              <div className={`w-full border-b my-1 border-blue-300/40`} />
              <label className="w-full">{rebalanceQuantityString(props.rebalanceItem)}</label>
              {/* Preço */}
              <label className="w-full text-nowrap pt-5">{'Valor (@R$ ' + positionPriceString(props.rebalanceItem.position) + ')'}</label>
              <div className={`w-full border-b my-1 border-blue-300/40`} />
              <label className="w-full">{"R$ " + rebalanceNotionalString(props.rebalanceItem)}</label>
              {/* % do Portfolio */}
              <label className="w-full pt-5">% do Portfolio</label>
              <div className={`w-full border-b my-1 border-blue-300/40`} />
              <label className="w-full text-transparent">.</label>
            </div>

            {/* Right (After Rebalance) Column */}
            <div className="flex flex-col w-1/3 text-end">
              <label className="w-full text-transparent">.</label>
              {/* Quantidade */}
              <label className="w-full pt-5">Depois</label>
              <div className={`w-full border-b my-1 border-blue-300/40 ${maskTransparentRightSide}`} />
              <label className="w-full">{positionQuantityString(newPosition)}</label>
              {/* Preço */}
              <label className="w-full pt-5">Depois</label>
              <div className={`w-full border-b my-1 border-blue-300/40 ${maskTransparentRightSide}`} />
              <label className="w-full">{"R$ " + positionNotionalString(newPosition)}</label>
              {/* % do Portfolio */}
              <label className="w-full pt-5">Depois</label>
              <div className={`w-full border-b my-1 border-blue-300/40 ${maskTransparentRightSide}`} />
              <label className="w-full">{componentPercentageString(newPosition)}</label>
            </div>
          </div>
        </td>
      </tr>
    </>
  )
}

export default PortfolioRebalanceItem;